import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography
} from "@mui/material";
import * as pdfjsLib from "pdfjs-dist/webpack";

const AssignmentModal = ({
  viewType,
  openPdf,
  onClose,
  application_id,
  assignment_file,
  assignment_file_type,
  submission_file,
  submission_file_type,
  offer_letter_file, 
}) => {
  const [assignmentImages, setAssignmentImages] = useState([]);
  const [submissionImages, setSubmissionImages] = useState([]);
  const [isAssignmentLoading, setIsAssignmentLoading] = useState(false);
  const [isSubmissionLoading, setIsSubmissionLoading] = useState(false);
  const [offerLetterImages, setOfferLetterImages] = useState([]);
  const [isOfferLoading, setIsOfferLoading] = useState(false);


  const renderPdf = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const images = [];
      
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const scale = 2;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        images.push(canvas.toDataURL("image/png"));
      }
      return images;
    } catch (error) {
      console.error("Error rendering PDF:", error);
      return [];
    }
  };

  useEffect(() => {
    const processAssignment = async () => {
      if (!assignment_file) return;
      setIsAssignmentLoading(true);
      
      try {
        if (assignment_file_type === "application/pdf") {
          const images = await renderPdf(assignment_file);
          setAssignmentImages(images);
        } else {
          setAssignmentImages([assignment_file]);
        }
      } finally {
        setIsAssignmentLoading(false);
      }
    };
    processAssignment();
  }, [assignment_file, assignment_file_type]);

  useEffect(() => {
    const processSubmission = async () => {
      if (!submission_file) return;
      setIsSubmissionLoading(true);
      
      try {
        if (submission_file_type === "application/pdf") {
          const images = await renderPdf(submission_file);
          setSubmissionImages(images);
        } else {
          setSubmissionImages([submission_file]);
        }
      } finally {
        setIsSubmissionLoading(false);
      }
    };
    processSubmission();
  }, [submission_file, submission_file_type]);


  useEffect(() => {
    const processOfferLetter = async () => {
      if (!offer_letter_file) return;
      setIsOfferLoading(true);
  
      try {
        const images = await renderPdf(offer_letter_file);
        setOfferLetterImages(images);
      } finally {
        setIsOfferLoading(false);
      }
    };
    processOfferLetter();
  }, [offer_letter_file]);

  const handleDownload = (file, type, prefix) => {
    if (!file) return;
    const extension = type === "application/pdf" ? "pdf" : "jpg";
    const link = document.createElement("a");
    link.href = file;
    link.download = `${prefix}_${application_id}.${extension}`;
    link.click();
  };

  return (
    <Dialog open={openPdf} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Document Preview</DialogTitle>
      
      <DialogContent dividers style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {/* Assignment Section */}
        {viewType === 'Assignment' && assignment_file && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Assignment Document</Typography>
              <Button 
                onClick={() => handleDownload(assignment_file, assignment_file_type, "assignment")} 
                variant="contained"
              >
                Download Assignment
              </Button>
            </Box>
            
            {isAssignmentLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : assignmentImages?.length > 0 ? (
              assignmentImages?.map((src, index) => (
                <img
                  key={`assignment-${index}`}
                  src={src}
                  alt={`Assignment Page ${index + 1}`}
                  style={{ width: "100%", marginBottom: 16 }}
                />
              ))
            ) : (
              <Typography color="textSecondary">No assignment document available</Typography>
            )}
          </Box>
        )}

        {/* Submission Section */}
        {viewType === 'submission' && submission_file && (
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6">Candidate Submission</Typography>
              <Button 
                onClick={() => handleDownload(submission_file, submission_file_type, "submission")} 
                variant="contained"
              >
                Download Submission
              </Button>
            </Box>
            
            {isSubmissionLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            ) : submissionImages?.length > 0 ? (
              submissionImages?.map((src, index) => (
                <img
                  key={`submission-${index}`}
                  src={src}
                  alt={`Submission Page ${index + 1}`}
                  style={{ width: "100%", marginBottom: 16 }}
                />
              ))
            ) : (
              <Typography color="textSecondary">No submission document available</Typography>
            )}
          </Box>
        )}

{viewType === 'offer' && offer_letter_file && (
  <Box sx={{ mt: 4 }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
      <Typography variant="h6">Offer Letter</Typography>
      <Button 
        onClick={() => handleDownload(offer_letter_file, "application/pdf", "offer_letter")} 
        variant="contained"
      >
        Download Offer Letter
      </Button>
    </Box>

    {isOfferLoading ? (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    ) : offerLetterImages?.length > 0 ? (
      offerLetterImages.map((src, index) => (
        <img
          key={`offer-${index}`}
          src={src}
          alt={`Offer Letter Page ${index + 1}`}
          style={{ width: "100%", marginBottom: 16 }}
        />
      ))
    ) : (
      <Typography color="textSecondary">No offer letter available</Typography>
    )}
  </Box>
)}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentModal;